import { useState } from "react";
import { useSchemaForm } from "@/components/form";
import { notify, notifyError } from "@/shared/notification";
import { useAddDocumentoElectronicoMutation } from "@/features/facturacion-electronica/api/add-documento-electronico";
import { useEmisorConfig } from "@/features/facturacion-electronica/hooks/use-emisor-config";
import { useEmisorData } from "@/features/facturacion-electronica/hooks/use-emisor-data";
import { AutofacturaForm, autofacturaFormSchema } from "@/features/facturacion-electronica/components/autofactura-form";
import { buildAutofacturaRequest } from "@/features/facturacion-electronica/utils/de-request-builder";
import { DEPageLayout } from "@/features/facturacion-electronica/components/de-page-layout";
import { DE_SUCCESS_MESSAGES } from "@/features/facturacion-electronica/utils/de-form-utils";
import type { VendedorData } from "@/features/facturacion-electronica/utils/de-types";
import { Cliente } from "@/shared/types/cliente";
import { openPdfFromBase64 } from "@/features/facturacion-electronica/utils/pdf-utils";
import useMonetizacion from "@/features/monetizacion/hooks/use-monetizacion";
import { Alert } from "@mui/material";
import { TipoDocumentoIdentidadEnum } from "@/shared/types/contribuyente";

export default function NuevaAutofacturaPage() {
    return (
        <DEPageLayout title="Nueva Autofactura">
            <AddAutofacturaForm />
        </DEPageLayout>
    );
}

// Helper para mapear Cliente a VendedorData
// El backend resuelve los códigos de ubicación desde el vendedorId
function mapClienteToVendedor(cliente: Cliente): VendedorData {
    return {
        id: cliente.id,
        ruc: cliente.ruc,
        dv: cliente.dv,
        numeroDocumento: cliente.numDocumento,
        razonSocial: cliente.nombre,
        direccion: cliente.direccion,
        numeroCasa: cliente.numeroCasa,
        email: cliente.email,
        tipoContribuyente: cliente.tipoContribuyente,
        tipoDocumento: cliente.tipoDocumento,
        tipoOperacion: cliente.tipoOperacion,
        naturaleza: cliente.naturaleza,
    };
}

function AddAutofacturaForm() {
    const mutation = useAddDocumentoElectronicoMutation();
    const { config } = useEmisorConfig();
    const { emisor } = useEmisorData();
    const [cliente, setCliente] = useState<Cliente | null>(null);
    const { monedaBase, monedas, cotizacion } = useMonetizacion();
    const tienePYG = monedas.some(m => m.simbolo === "PYG");
    const clienteEsInnominado = cliente?.tipoDocumento === TipoDocumentoIdentidadEnum.Innominado;

    const form = useSchemaForm({
        schema: autofacturaFormSchema,
        onSubmit: async ({ value, formApi }) => {
            if (!emisor) {
                notifyError("No se pudo obtener la información del emisor");
                return;
            }

            if (!cliente) {
                notifyError("Debe seleccionar un cliente");
                return;
            }

            // en autofactura el cliente necesita tener departamento, ciuad y direccion
            if (!cliente.departamento?.id || !cliente.ciudad?.id) {
                notifyError("El cliente  debe tener Departamento y Ciudad configurados. Edite el contribuyente para agregar estos datos.");
                return;
            }

            if (!cliente.direccion) {
                notifyError("El cliente debe tener una dirección configurada.");
                return;
            }

            if (cliente.tipoDocumento === TipoDocumentoIdentidadEnum.Innominado) {
                notifyError("El cliente tiene tipo de documento Innominado (5), lo cual no está permitido para autofacturas.");
                return;
            }

            const clienteVendedorData = mapClienteToVendedor(cliente);
            const deRequest = buildAutofacturaRequest(value, config!, emisor.id, clienteVendedorData, monedaBase, monedas, cotizacion);
            const result = await mutation.mutateAsync(deRequest);
            notify(DE_SUCCESS_MESSAGES.autofactura);

            if (result.kuDEBase64)
                openPdfFromBase64(result.kuDEBase64);

            // Reset form 
            formApi.reset();
            setCliente(null);
            formApi.setFieldValue("items", []);
            formApi.setFieldValue("vendedorId", "");
            formApi.setFieldValue("condicionOperacion", "contado");
            formApi.setFieldValue("pagos", [{ tipoPago: "efectivo", monto: 0 }]);
            formApi.setFieldValue("tipoDocumentoAsociado", "3");
        },
    });

    // Verificar si el vendedor tiene datos incompletos para autofactura
    const clienteIncompleto = cliente ? (!cliente.departamento?.id || !cliente.ciudad?.id || !cliente.direccion) : false;

    return (
        <>
            {!tienePYG && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    No hay ninguna moneda con código PYG configurada. La autofactura solo puede emitirse en Guaraníes (PYG). Configurá la moneda en Monetización.
                </Alert>
            )}
            {clienteEsInnominado && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    El cliente seleccionado tiene tipo de documento Innominado (5), lo cual no está permitido para autofacturas.
                </Alert>
            )}
            <AutofacturaForm
                form={form}
                emisor={emisor || null}
                onClienteChange={setCliente}
                cliente={cliente}
                clienteIncompleto={clienteIncompleto}
                flex={1}
            />
        </>
    );
}
